/**
 * Session return-location helpers.
 *
 * When the user explicitly chooses to stay signed in from the session-timeout
 * warning, we want to bring them back to the page they were on after logging in
 * again. Since there is no silent token renewal, "Continue" re-logs the user in,
 * so we stash the pre-logout path in localStorage and consume it (one-shot)
 * after the next login. Plain expiry and normal logout do NOT stash a path — a
 * re-login in those cases lands on the default home page.
 *
 * Everything is validated as a SAME-ORIGIN, safe relative path to avoid open
 * redirect issues: only absolute in-app paths (starting with a single `/`) are
 * accepted, and auth/logout paths are never stored (they would create loops).
 */

export const SESSION_RETURN_LOCATION_KEY = 'session_return_location';

/** Route prefixes we must never return the user to (would loop them out). */
const BLOCKED_PATH_PREFIXES = ['/auth', '/logout'];

/**
 * True if `pathname` is (or lives under) any blocked prefix. Segment-anchored so
 * legitimate paths like `/authoring` or `/course/1/authors` are NOT blocked —
 * only `/auth`, `/auth/...`, `/logout`, `/logout/...`.
 */
function isBlockedPath(pathname: string): boolean {
  return BLOCKED_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Validates that a value is a safe, same-origin, in-app relative path.
 *
 * Rejects:
 *  - empty / non-string values, or values with control characters / backslashes
 *  - values that do not start with `/`
 *  - protocol-relative URLs (`//host`) and normalization tricks (`/\host`,
 *    tab/newline injection) that browsers resolve OFF-origin — validated by
 *    resolving against the current origin and asserting it stays same-origin
 *  - values containing a URL scheme (e.g. `http:`, `javascript:`)
 *  - auth/logout paths (would loop the user straight back out)
 */
function isSafeReturnPath(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;

  // Reject control chars and backslashes: browsers strip/normalize these and can
  // turn `/\evil.com` or tab-injected values into protocol-relative URLs.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\\]/.test(value)) return false;

  // Must be an in-app absolute path, never protocol-relative (`//evil.com`).
  if (!value.startsWith('/') || value.startsWith('//')) return false;

  // Reject any explicit scheme (http:, javascript:, data:, ...).
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return false;

  // Resolve against the current origin: any value that lands off-origin once the
  // browser's URL parser has its way (the definitive open-redirect guard) fails.
  try {
    const resolved = new URL(value, globalThis.location.origin);
    if (resolved.origin !== globalThis.location.origin) return false;
    // Never return the user to auth/logout screens (segment-anchored).
    if (isBlockedPath(resolved.pathname)) return false;
  } catch {
    return false;
  }

  return true;
}

/**
 * Persists the given same-origin path (pathname + optional search) so it can be
 * restored after the next login. No-ops on unsafe or auth/logout paths.
 *
 * @param pathname - In-app absolute path, e.g. `/course/123/home?tab=1`.
 */
export function saveReturnLocation(pathname: string): void {
  if (!isSafeReturnPath(pathname)) return;

  try {
    globalThis.localStorage.setItem(SESSION_RETURN_LOCATION_KEY, pathname);
  } catch {
    // localStorage may be unavailable (private mode / quota) — fail silently.
  }
}

/**
 * Reads and removes the stored return location (one-shot). Returns the path only
 * if it still passes the safe/same-origin check, otherwise `null`.
 */
export function consumeReturnLocation(): string | null {
  let stored: string | null = null;

  try {
    stored = globalThis.localStorage.getItem(SESSION_RETURN_LOCATION_KEY);
    globalThis.localStorage.removeItem(SESSION_RETURN_LOCATION_KEY);
  } catch {
    return null;
  }

  return isSafeReturnPath(stored) ? stored : null;
}
